/**
 * Default Sidebar Preset for Agentela Design System
 * 
 * This preset provides a complete, ready-to-use sidebar configuration
 * that can be easily customized for different platforms and use cases.
 */

import type { SidebarConfig } from '../../types/sidebar';

export const defaultSidebarPreset: SidebarConfig = {
  brand: {
    name: "Agentela",
    subtitle: "Engineering Platform",
    size: "default"
  },
  standaloneItems: [
    {
      id: "overview",
      label: "Overview",
      icon: "Home",
      href: "/dashboard",
      isActive: false
    }
  ],
  groups: [
    {
      id: "development",
      label: "Development",
      icon: "GitBranch",
      themeColor: "agentela-gold",
      items: [
        {
          id: "sprints",
          label: "Sprints & Planning",
          icon: "Target",
          href: "/sprints",
          isActive: false
        },
        {
          id: "ai-plans",
          label: "AI Engineering Plans",
          icon: "BrainCircuit",
          href: "/ai-plans",
          isActive: false,
          badge: {
            text: "Beta",
            variant: "default"
          }
        },
        {
          id: "testing",
          label: "Testing & Quality",
          icon: "Beaker",
          href: "/testing",
          isActive: false,
          badge: {
            text: "3",
            variant: "secondary"
          }
        },
        {
          id: "repositories",
          label: "Repositories",
          icon: "GitCommit",
          href: "/repositories",
          isActive: false
        }
      ]
    },
    {
      id: "team",
      label: "Team & People",
      icon: "Users",
      themeColor: "agentela-purple",
      items: [
        {
          id: "team-management",
          label: "Team Management",
          icon: "Users",
          href: "/team",
          isActive: false
        },
        {
          id: "onboarding",
          label: "Onboarding",
          icon: "UserPlus",
          href: "/onboarding",
          isActive: false,
          badge: {
            text: "New",
            variant: "outline"
          }
        },
        {
          id: "training",
          label: "Training",
          icon: "BookOpen",
          href: "/training",
          isActive: false
        }
      ]
    },
    {
      id: "review",
      label: "Review & Quality",
      icon: "CheckSquare",
      themeColor: "agentela-blue",
      items: [
        {
          id: "gold-standard",
          label: "Gold Standard Tracking",
          icon: "Award",
          href: "/gold-standard",
          isActive: false
        },
        {
          id: "quality-gates",
          label: "Quality Gates",
          icon: "Shield",
          href: "/quality-gates",
          isActive: false
        },
        {
          id: "approvals",
          label: "Approvals",
          icon: "CheckCircle",
          href: "/approvals",
          isActive: false,
          badge: {
            text: "!",
            variant: "destructive"
          }
        }
      ]
    }
  ],
  user: {
    name: "John Doe",
    role: "Engineering Lead",
    initials: "JD",
    avatar: "https://github.com/shadcn.png"
  }
};

/**
 * Customization helpers for the default preset
 */

export const customizeDefaultSidebar = (overrides: Partial<SidebarConfig>): SidebarConfig => {
  return {
    brand: {
      ...defaultSidebarPreset.brand,
      ...overrides.brand
    },
    standaloneItems: overrides.standaloneItems || defaultSidebarPreset.standaloneItems,
    groups: overrides.groups || defaultSidebarPreset.groups,
    user: {
      ...defaultSidebarPreset.user,
      ...overrides.user
    }
  };
};

/**
 * Quick brand customization
 */
export const withCustomBrand = (brandConfig: Partial<SidebarConfig['brand']>): SidebarConfig => {
  return {
    ...defaultSidebarPreset,
    brand: {
      ...defaultSidebarPreset.brand,
      ...brandConfig
    }
  };
};

/**
 * Quick user customization
 */
export const withCustomUser = (userConfig: Partial<SidebarConfig['user']>): SidebarConfig => {
  return {
    ...defaultSidebarPreset,
    user: {
      ...defaultSidebarPreset.user,
      ...userConfig
    }
  };
};

/**
 * Create a current user configuration from common user data sources
 */
export const createCurrentUser = (userData: {
  name?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  role?: string;
  title?: string;
  position?: string;
  avatar?: string;
  profilePicture?: string;
  imageUrl?: string;
}): SidebarConfig['user'] => {
  // Determine the display name
  const name = userData.name || 
               userData.displayName || 
               (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
               userData.firstName ||
               userData.email?.split('@')[0] ||
               'User';

  // Determine the role/title
  const role = userData.role || userData.title || userData.position || 'User';

  // Generate initials from the name
  const initials = name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');

  // Determine the avatar URL
  const avatar = userData.avatar || userData.profilePicture || userData.imageUrl;

  return {
    name,
    role,
    initials,
    avatar
  };
};

/**
 * Create user menu actions with common patterns
 */
export const createUserMenuActions = (options: {
  onAccountSettings?: () => void;
  onProfile?: () => void;
  onBilling?: () => void;
  onSubscription?: () => void;
  onNotifications?: () => void;
  onPreferences?: () => void;
  onHelp?: () => void;
  onSupport?: () => void;
  onLogout?: () => void;
  onSignOut?: () => void;
  customActions?: Array<{
    label: string;
    onClick: () => void;
    icon?: string;
  }>;
}) => {
  const actions: any = {};
  
  // Map different naming conventions to our standard names
  if (options.onAccountSettings || options.onProfile) {
    actions.onAccountSettings = options.onAccountSettings || options.onProfile;
  }
  
  if (options.onBilling || options.onSubscription) {
    actions.onBilling = options.onBilling || options.onSubscription;
  }
  
  if (options.onNotifications || options.onPreferences) {
    actions.onNotifications = options.onNotifications || options.onPreferences;
  }
  
  if (options.onLogout || options.onSignOut) {
    actions.onLogout = options.onLogout || options.onSignOut;
  }

  // Add custom actions including help/support if provided
  const customActions = [...(options.customActions || [])];
  
  if (options.onHelp) {
    customActions.push({
      label: "Help & Support",
      onClick: options.onHelp,
      icon: "BookOpen"
    });
  }
  
  if (options.onSupport && !options.onHelp) {
    customActions.push({
      label: "Support",
      onClick: options.onSupport,
      icon: "MessageCircle"
    });
  }

  if (customActions.length > 0) {
    actions.customActions = customActions;
  }

  return actions;
};
